/**
 * Presence (Phase 3, task 5): while the reviewer has a report open, its status is
 * 'reviewing'; it reverts to 'checked' if they leave the tab, close it, or go idle for a
 * while. Marking a report 'done' is a separate, explicit reviewer action (see
 * ReportWorkspace) — presence never sets or clears 'done'.
 */
import { useEffect } from 'react';
import { setReportStatus } from '../api';

const IDLE_MS = 5 * 60 * 1000;

export function useReviewerPresence(reportId: string, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    void setReportStatus(reportId, 'reviewing').catch(() => {});

    let idleTimer: number | undefined;
    let idle = false;

    const markActive = () => {
      if (idle) {
        idle = false;
        void setReportStatus(reportId, 'reviewing').catch(() => {});
      }
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        idle = true;
        void setReportStatus(reportId, 'checked').catch(() => {});
      }, IDLE_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void setReportStatus(reportId, 'checked', { keepalive: true }).catch(() => {});
      } else {
        markActive();
      }
    };

    markActive();
    window.addEventListener('mousemove', markActive);
    window.addEventListener('keydown', markActive);
    window.addEventListener('scroll', markActive, true);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('scroll', markActive, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void setReportStatus(reportId, 'checked', { keepalive: true }).catch(() => {});
    };
  }, [reportId, enabled]);
}
